import React, { ReactNode } from "react";
import {
  Dialog,
  DialogTitle,
  Divider,
  Slide,
  Theme,
  Typography,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { TransitionProps } from "@mui/material/transitions";
import { Breakpoint, SxProps } from "@mui/system";
import { useTranslation } from "react-i18next";

import AppScrollbar from "./AppScrollbar";
import { BackIcon } from "../../constants/icons";
import { getSymbolLogo } from "../../utils/tradeUtils";
import { colors } from "../../constants/colors";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface AppDialogProps {
  maxWidth?: Breakpoint;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string | ReactNode;
  dividers?: boolean;
  hideClose?: boolean;
  fullHeight?: boolean;
  actionTitle?: string;
  sxStyle?: SxProps<Theme>;
  fullScreen?: boolean;
  showDivider?: boolean;
  backgroundColor?: string;
  leftHeader?: boolean;
  tradeDetail?: any;
}

const AppDialog: React.FC<AppDialogProps> = ({
  sxStyle,
  maxWidth = "sm",
  hideClose = false,
  open,
  onClose,
  children,
  dividers = false,
  title,
  actionTitle,
  fullHeight = false,
  fullScreen = false,
  showDivider = true,
  backgroundColor,
  leftHeader = false,
  tradeDetail,
}) => {
  const { t } = useTranslation();
  return (
    <Dialog
      fullScreen={fullScreen}
      sx={{
        "& .MuiDialog-paper": {
          width: "100%",
          background: backgroundColor,
        },
        "& .MuiDialogContent-root": {
          overflowY: "hidden",
          paddingLeft: 0,
          paddingRight: 0,
        },
        ...sxStyle,
      }}
      maxWidth={maxWidth}
      TransitionComponent={Transition}
      open={open}
      onClose={onClose}
    >
      <DialogTitle sx={{ fontSize: 14, fontWeight: 500 }}>
        {leftHeader ? (
          <>
            <IconButton
              aria-label="close"
              sx={{
                position: "absolute",
                left: 4,
                top: 4,
                color: "grey.500",
              }}
              onClick={onClose}
              size="large"
            >
              <BackIcon color="white" size={25} />
            </IconButton>
            {tradeDetail ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginLeft: 30,
                }}
              >
                <img
                  src={getSymbolLogo(
                    tradeDetail.symbol,
                    tradeDetail.symbolLogo
                  )}
                  alt="symbol"
                  style={{
                    borderRadius: tradeDetail?.symbolLogo ? "50%" : "0",
                    width: 30,
                    height: 30,
                  }}
                />
                <div style={{ marginLeft: 10 }}>
                  <Typography color="white">
                    {tradeDetail.symbolName}
                  </Typography>
                  <Typography variant="body2" color={colors.darkGrey}>
                    {t("binary")}
                  </Typography>
                </div>
              </div>
            ) : (
              <Typography variant="body1" sx={{ color: "white", ml: 4 }}>
                {title}
              </Typography>
            )}
          </>
        ) : (
          <>
            {" "}
            <Typography textAlign="center" variant="body1">
              {title}
            </Typography>
            {hideClose ? null : (
              <IconButton
                aria-label="close"
                sx={{
                  position: "absolute",
                  right: 4,
                  top: 4,
                  color: "grey.500",
                }}
                onClick={onClose}
                size="large"
              >
                <CloseIcon />
              </IconButton>
            )}
          </>
        )}
      </DialogTitle>
      <Divider sx={{ padding: "10px 0" }} />
      <DialogContent
        dividers={dividers}
        sx={{
          paddingTop: 1,
          height: fullHeight ? "90vh" : "auto",
          minHeight: "300px",
          maxHeight: "99vh", // Adjust maxHeight to control scroll
          paddingRight: 2,
          paddingLeft: 2,
          overflowY: "scroll", // Prevent overflow on DialogContent
        }}
      >
        <AppScrollbar
          sx={{
            height: "calc(100% - 20px)", // Adjust height to fit within the container
            maxHeight: "calc(100% - 20px)", // Adjust maxHeight to fit within the container
          }}
        >
          {children}
        </AppScrollbar>
      </DialogContent>
      {actionTitle ? (
        <DialogActions>
          <Button color="primary" variant="contained" type="submit">
            {actionTitle}
          </Button>
        </DialogActions>
      ) : null}
    </Dialog>
  );
};
export default AppDialog;
